import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { chartToneVar, SpecraChartFrame } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
const NODE_W = 10;
const NODE_GAP = 12;
const MIN_NODE_H = 2;
/**
 * Columns by longest path from the sources (a link that closes a cycle is
 * dropped), node height in proportion to max(in, out), ribbons stacked in
 * the order of the node they meet. Pure and deterministic.
 */ export function layoutSankey(links, width, height) {
    const valid = links.filter((l)=>l.from !== l.to && Number.isFinite(l.value) && l.value > 0 && l.from && l.to);
    const order = [];
    const seen = new Set();
    for (const l of valid){
        for (const id of [
            l.from,
            l.to
        ]){
            if (!seen.has(id)) {
                seen.add(id);
                order.push(id);
            }
        }
    }
    // Drop links that close a cycle (DFS in input order).
    const byFrom = new Map();
    for (const l of valid)byFrom.set(l.from, [
        ...byFrom.get(l.from) ?? [],
        l
    ]);
    const state = new Map();
    const kept = [];
    const visit = (id)=>{
        state.set(id, 1);
        for (const l of byFrom.get(id) ?? []){
            const s = state.get(l.to);
            if (s === 1) continue;
            kept.push(l);
            if (!s) visit(l.to);
        }
        state.set(id, 2);
    };
    for (const id of order)if (!state.get(id)) visit(id);
    const into = new Map();
    const out = new Map();
    for (const id of order){
        into.set(id, []);
        out.set(id, []);
    }
    for (const l of kept){
        out.get(l.from)?.push(l);
        into.get(l.to)?.push(l);
    }
    const column = new Map();
    const depth = (id)=>{
        const known = column.get(id);
        if (known !== undefined) return known;
        const parents = into.get(id) ?? [];
        const d = parents.length ? Math.max(...parents.map((l)=>depth(l.from))) + 1 : 0;
        column.set(id, d);
        return d;
    };
    for (const id of order)depth(id);
    const columns = Math.max(0, ...order.map((id)=>(column.get(id) ?? 0) + 1));
    if (!columns) return {
        nodes: [],
        ribbons: [],
        columns: 0
    };
    const sum = (ls)=>ls.reduce((a, l)=>a + l.value, 0);
    const value = new Map(order.map((id)=>[
            id,
            Math.max(sum(into.get(id) ?? []), sum(out.get(id) ?? []))
        ]));
    const cols = Array.from({
        length: columns
    }, ()=>[]);
    for (const id of order)cols[column.get(id) ?? 0].push(id);
    const k = Math.min(...cols.map((c)=>{
        const total = c.reduce((a, id)=>a + (value.get(id) ?? 0), 0);
        return total > 0 ? (height - (c.length - 1) * NODE_GAP) / total : Number.POSITIVE_INFINITY;
    }));
    const step = columns > 1 ? (width - NODE_W) / (columns - 1) : 0;
    const nodes = new Map();
    cols.forEach((c, ci)=>{
        if (ci > 0) {
            // Order by where the column's sources sit, to keep ribbons from crossing.
            const centre = (id)=>{
                const ins = into.get(id) ?? [];
                const total = sum(ins);
                if (!total) return 0;
                return ins.reduce((a, l)=>{
                    const n = nodes.get(l.from);
                    return a + (n ? n.y + n.h / 2 : 0) * l.value;
                }, 0) / total;
            };
            c.sort((a, b)=>centre(a) - centre(b));
        }
        const hs = c.map((id)=>Math.max(MIN_NODE_H, (value.get(id) ?? 0) * k));
        const used = hs.reduce((a, h)=>a + h, 0) + (c.length - 1) * NODE_GAP;
        let y = (height - used) / 2;
        c.forEach((id, i)=>{
            nodes.set(id, {
                id,
                column: ci,
                value: value.get(id) ?? 0,
                x: columns > 1 ? ci * step : (width - NODE_W) / 2,
                y,
                w: NODE_W,
                h: hs[i]
            });
            y += hs[i] + NODE_GAP;
        });
    });
    const outOffset = new Map();
    const inOffset = new Map();
    const centreY = (id)=>{
        const n = nodes.get(id);
        return n ? n.y + n.h / 2 : 0;
    };
    // Stack each node's ribbons in the order of the node at their other end.
    const sorted = [
        ...kept
    ].sort((a, b)=>centreY(a.to) - centreY(b.to) || centreY(a.from) - centreY(b.from));
    const outY = new Map();
    for (const l of sorted){
        const n = nodes.get(l.from);
        if (!n) continue;
        const w = l.value * k;
        const off = outOffset.get(l.from) ?? 0;
        outY.set(l, n.y + off + w / 2);
        outOffset.set(l.from, off + w);
    }
    const inY = new Map();
    for (const l of [
        ...kept
    ].sort((a, b)=>centreY(a.from) - centreY(b.from))){
        const n = nodes.get(l.to);
        if (!n) continue;
        const w = l.value * k;
        const off = inOffset.get(l.to) ?? 0;
        inY.set(l, n.y + off + w / 2);
        inOffset.set(l.to, off + w);
    }
    const ribbons = kept.flatMap((l)=>{
        const a = nodes.get(l.from);
        const b = nodes.get(l.to);
        const sy = outY.get(l);
        const ty = inY.get(l);
        if (!a || !b || sy === undefined || ty === undefined) return [];
        const x0 = a.x + a.w;
        const x1 = b.x;
        const mid = (x0 + x1) / 2;
        return [
            {
                ...l,
                width: Math.max(1, l.value * k),
                d: `M${x0},${sy} C${mid},${sy} ${mid},${ty} ${x1},${ty}`
            }
        ];
    });
    return {
        nodes: order.flatMap((id)=>nodes.get(id) ?? []),
        ribbons,
        columns
    };
}
const PLOT_W = 640;
const LABEL_W = 168;
const defaultFormat = (v)=>v.toLocaleString('en-US');
/** A flow of amounts between named things, drawn as proportional ribbons. */ export function SpecraSankey({ title, description, insight, links, format = defaultFormat, source, className }) {
    const valid = React.useMemo(()=>links.filter((l)=>l.value > 0 && l.from !== l.to), [
        links
    ]);
    const plotH = React.useMemo(()=>{
        const counts = new Map();
        for (const l of valid)counts.set(l.from, (counts.get(l.from) ?? 0) + 1);
        const busiest = Math.max(1, ...counts.values(), new Set(valid.map((l)=>l.to)).size / 2);
        return Math.min(480, Math.max(180, busiest * 40));
    }, [
        valid
    ]);
    const innerW = PLOT_W - 2 * LABEL_W;
    const layout = React.useMemo(()=>layoutSankey(valid, innerW, plotH), [
        valid,
        innerW,
        plotH
    ]);
    // A hovered or focused ribbon lights itself and the two nodes it joins.
    const [hover, setHover] = React.useState(null);
    const ribbonActive = (i)=>!hover || hover.ribbon === i;
    const nodeActive = (id)=>!hover || layout.ribbons[hover.ribbon]?.from === id || layout.ribbons[hover.ribbon]?.to === id;
    const table = {
        columns: [
            {
                key: 'from',
                label: 'From'
            },
            {
                key: 'to',
                label: 'To'
            },
            {
                key: 'value',
                label: 'Value',
                numeric: true
            }
        ],
        rows: layout.ribbons.map((r, i)=>({
                id: `${r.from}->${r.to}-${i}`,
                cells: {
                    from: r.from,
                    to: r.to,
                    value: format(r.value)
                }
            }))
    };
    return /*#__PURE__*/ _jsxs(SpecraChartFrame, {
        className: className,
        description: description,
        insight: insight,
        table: table,
        title: title,
        children: [
            /*#__PURE__*/ _jsx(SpecraTooltipProvider, {
                delayDuration: 100,
                children: /*#__PURE__*/ _jsx("svg", {
                    "aria-label": `${title}: ${layout.ribbons.length} flows`,
                    className: "h-auto w-full text-foreground",
                    "data-sankey": "",
                    role: "img",
                    viewBox: `0 0 ${PLOT_W} ${plotH}`,
                    children: /*#__PURE__*/ _jsxs("g", {
                        transform: `translate(${LABEL_W},0)`,
                        children: [
                            layout.ribbons.map((r, i)=>/*#__PURE__*/ _jsx(SpecraTooltip, {
                                    content: /*#__PURE__*/ _jsxs("span", {
                                        className: "block",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "block font-semibold",
                                                children: format(r.value)
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "block",
                                                children: [
                                                    r.from,
                                                    " → ",
                                                    r.to
                                                ]
                                            })
                                        ]
                                    }),
                                    children: /*#__PURE__*/ _jsx("path", {
                                        "aria-label": `${r.from} to ${r.to}: ${format(r.value)}`,
                                        className: cn('cursor-default outline-none transition-[stroke-opacity] duration-[var(--dur-fast)]'),
                                        d: r.d,
                                        "data-sankey-ribbon": "",
                                        fill: "none",
                                        onBlur: ()=>setHover(null),
                                        onFocus: ()=>setHover({
                                                ribbon: i
                                            }),
                                        onMouseEnter: ()=>setHover({
                                                ribbon: i
                                            }),
                                        onMouseLeave: ()=>setHover(null),
                                        stroke: chartToneVar[r.tone ?? 'series'],
                                        strokeOpacity: ribbonActive(i) ? hover ? 0.6 : 0.35 : 0.1,
                                        strokeWidth: r.width,
                                        tabIndex: 0
                                    })
                                }, `${r.from}->${r.to}-${i}`)),
                            layout.nodes.map((n)=>{
                                const last = n.column === layout.columns - 1 && layout.columns > 1;
                                return /*#__PURE__*/ _jsxs("g", {
                                    "data-sankey-node": "",
                                    opacity: nodeActive(n.id) ? 1 : 0.35,
                                    children: [
                                        /*#__PURE__*/ _jsx("rect", {
                                            fill: chartToneVar.series,
                                            height: n.h,
                                            rx: 2,
                                            width: n.w,
                                            x: n.x,
                                            y: n.y
                                        }),
                                        /*#__PURE__*/ _jsxs("text", {
                                            className: "fill-current text-[length:var(--fs-ui-caption)]",
                                            dominantBaseline: "middle",
                                            textAnchor: last ? 'start' : 'end',
                                            x: last ? n.x + n.w + 6 : n.x - 6,
                                            y: n.y + n.h / 2,
                                            children: [
                                                n.id.length > 22 ? `${n.id.slice(0, 21)}…` : n.id,
                                                /*#__PURE__*/ _jsxs("tspan", {
                                                    className: "fill-muted-foreground",
                                                    children: [
                                                        " ",
                                                        format(n.value)
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }, n.id);
                            })
                        ]
                    })
                })
            }),
            source ? /*#__PURE__*/ _jsx("p", {
                className: "m-0 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: source
            }) : null
        ]
    });
}
